import { Toaster as SonnerToaster } from "sonner";

/**
 * App-wide toast host. Type-specific colors match the Alert tinted variants
 * 1-for-1 (success/info/warning/danger) so a "Synced" toast and a "Synced"
 * Alert look like the same brand voice.
 *
 * The colors live inside the base `toast` className via [data-type=…]
 * attribute selectors instead of sonner's per-type classNames map. That
 * pattern wins on natural CSS specificity (class + attribute beats the base
 * class alone) so no `!important` is needed and overrides are predictable.
 *
 * Usage:
 *   import { toast } from "sonner";
 *   toast.success("Synced");
 *   toast.error("Sync failed", { description: "Snowflake returned 5xx" });
 *   toast("Heads up", { duration: Infinity });   // persistent, must close
 *
 * The close X is always rendered (sonner `closeButton`).
 */
export function Toaster() {
  return (
    <SonnerToaster
      position="bottom-right"
      closeButton
      toastOptions={{
        classNames: {
          // Base + per-type tinted backgrounds + matching border + text colors.
          // Default (no data-type) uses popover surface.
          toast: [
            "group toast border rounded-lg shadow-lg",
            // Default surface — neutral popover when no specific type.
            "bg-popover text-popover-foreground border-border",
            // Type-specific overrides. Attribute-selector specificity is
            // (class + attr) which beats the bare class above, so these win
            // naturally without !important.
            "[&[data-type=info]]:bg-info-light [&[data-type=info]]:text-info [&[data-type=info]]:border-info/40",
            "[&[data-type=success]]:bg-success-light [&[data-type=success]]:text-success [&[data-type=success]]:border-success/40",
            "[&[data-type=warning]]:bg-warning-light [&[data-type=warning]]:text-warning [&[data-type=warning]]:border-warning/40",
            "[&[data-type=error]]:bg-danger-light [&[data-type=error]]:text-danger [&[data-type=error]]:border-danger/40",
          ].join(" "),
          title: "font-semibold",
          // Descriptions get a slightly muted version of the type color so
          // the title/description hierarchy is preserved per-tone.
          description: [
            "text-muted-foreground",
            "[[data-type=info]_&]:text-info/80",
            "[[data-type=success]_&]:text-success/80",
            "[[data-type=warning]_&]:text-warning/80",
            "[[data-type=error]_&]:text-danger/80",
          ].join(" "),
          actionButton: "bg-power text-white",
          cancelButton: "bg-muted text-muted-foreground",
          closeButton:
            "bg-background border-border text-muted-foreground hover:text-foreground",
        },
      }}
    />
  );
}
